import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BadgeRow } from "@/components/Badges";
import { StatusPill } from "@/components/StatusTracker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, FileText, Umbrella, Upload, Landmark, Sparkles } from "lucide-react";

const TRADES = ["Plumbing", "Electrical", "HVAC", "Roofing", "Carpentry", "Painting", "Flooring", "Masonry", "Landscaping", "Concrete", "Renovation", "General Contracting", "Cleaning"];
const AREAS = ["Toronto", "Mississauga", "Brampton", "Scarborough", "Markham", "Vaughan", "Hamilton", "Ottawa", "North York", "Etobicoke"];
const DOC_TYPES = [
  { key: "license", label: "Business / Trade License" },
  { key: "insurance", label: "Proof of Insurance (certificate + expiry)" },
  { key: "background_check", label: "Background Check" },
  { key: "ownership", label: "Black-Ownership Documentation" },
];

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function BusinessDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [docs, setDocs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);

  const load = useCallback(async () => {
    setProfile(await api.get("/businesses/me").then((r) => r.data).catch(() => null));
    setDocs(await api.get("/documents/me").then((r) => r.data).catch(() => []));
    setJobs(await api.get("/jobs/matching").then((r) => r.data).catch(() => []));
    setMyJobs(await api.get("/jobs").then((r) => r.data).catch(() => []));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!profile) return <div className="max-w-5xl mx-auto px-4 py-16 text-slate-400">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-950">{profile.business_name}</h1>
          <div className="mt-2 flex items-center gap-3">
            <StatusPill status={profile.status === "approved" ? "completed" : profile.status === "pending" ? "quoted" : "requested"} />
            <span className="text-sm text-slate-500 capitalize">{profile.status}</span>
          </div>
        </div>
        <BadgeRow badges={profile.badges} />
      </div>

      {profile.status === "pending" && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 flex items-center gap-2">
          <Clock size={16} /> Your profile is under admin review. You'll be notified once vetting is complete.
        </div>
      )}
      {profile.status === "rejected" && (
        <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-800">
          <strong>Application needs attention.</strong> {profile.reason} — update your documents and re-submit.
        </div>
      )}

      {profile.status === "approved" && <FeaturedCard profile={profile} onChanged={load} />}
      {profile.status === "approved" && <VisibilityStats profile={profile} />}

      <Tabs defaultValue="profile" className="mt-8">
        <TabsList data-testid="business-tabs">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-matching-jobs">Matching Jobs</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">My Jobs & Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="profile"><ProfileTab profile={profile} onSaved={load} /></TabsContent>
        <TabsContent value="documents"><DocsTab docs={docs} profile={profile} onChanged={load} /></TabsContent>
        <TabsContent value="jobs"><JobsTab jobs={jobs} profile={profile} onQuoted={load} /></TabsContent>
        <TabsContent value="payouts"><PayoutsTab myJobs={myJobs} profile={profile} onChanged={load} /></TabsContent>
      </Tabs>
    </div>
  );
}

function VisibilityStats({ profile }) {
  const s = profile.stats || {};
  const rows = [
    { label: "Profile views", total: s.profile_views || 0, feat: s.featured_profile_views || 0, testid: "stat-profile-views" },
    { label: "Search & match appearances", total: s.impressions || 0, feat: s.featured_impressions || 0, testid: "stat-impressions" },
    { label: "Quote invites received", total: s.invites || 0, feat: s.featured_invites || 0, testid: "stat-invites" },
  ];
  return (
    <div data-testid="visibility-stats" className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
      <h3 className="font-heading font-bold text-slate-900">Your visibility</h3>
      <p className="mt-1 text-sm text-slate-500">Lifetime totals, with the share earned while featured.</p>
      <div className="mt-4 grid sm:grid-cols-3 gap-4">
        {rows.map((r) => (
          <div key={r.label} data-testid={r.testid} className="rounded-xl bg-slate-50 border border-slate-100 p-4">
            <p className="text-3xl font-heading font-extrabold text-slate-950">{r.total.toLocaleString()}</p>
            <p className="mt-1 text-xs font-medium text-slate-600">{r.label}</p>
            <p className="mt-1 text-xs text-amber-700 font-semibold">{r.feat.toLocaleString()} while featured</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeaturedCard({ profile, onChanged }) {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [avail, setAvail] = useState(null);
  useEffect(() => {
    api.get("/featured/plans").then((r) => { setPlans(r.data.plans || []); setSub(r.data.subscription || null); }).catch(() => {});
    api.get("/featured/availability").then((r) => setAvail(r.data)).catch(() => {});
  }, []);
  const errMsg = (e, fallback) => {
    const d = e?.response?.data?.detail;
    return typeof d === "string" && d.length < 160 ? d : fallback;
  };
  const buy = async (planId) => {
    setBusy(planId);
    try {
      const r = await api.post("/businesses/me/feature/checkout", { plan: planId, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(errMsg(e, "Could not start checkout — please try again.")); setBusy(""); }
  };
  const cancelSub = async () => {
    setCancelling(true);
    try {
      await api.post("/businesses/me/feature/cancel");
      toast.success("Auto-renew cancelled — you stay featured until the period ends.");
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e, "Could not cancel right now — please try again.")); }
    finally { setCancelling(false); }
  };
  const subInfo = profile.featured_subscription;
  const subActive = subInfo && subInfo.status === "active";
  const willCancel = subInfo && subInfo.cancel_at_period_end;
  const soldOut = avail && !avail.can_purchase;
  return (
    <div data-testid="featured-card" className="mt-4 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6">
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="text-amber-500" />
        <h3 className="font-heading font-bold text-slate-900">Featured Placement</h3>
        {profile.featured && <span data-testid="featured-active-badge" className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500 text-white">Active</span>}
      </div>
      <p className="mt-1 text-sm text-slate-600">
        Get seen first — appear at the top of Browse results and client job matches with a Featured badge.
        {profile.featured && profile.featured_until ? ` Active until ${new Date(profile.featured_until).toLocaleDateString()}.` : ""}
      </p>

      {avail && avail.cities && avail.cities.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="featured-slots">
          {avail.cities.map((c) => (
            <span key={c.city} data-testid={`slot-${c.city}`}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c.open > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
              {c.city}: {c.open > 0 ? `${c.open} of ${c.cap} slots open` : "full"}
            </span>
          ))}
        </div>
      )}

      {subActive ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-white/70 p-4" data-testid="subscription-status">
          <p className="text-sm font-semibold text-slate-900">Monthly auto-renew is on{sub ? ` — $${sub.amount} CAD/mo` : ""}</p>
          {willCancel
            ? <p className="mt-1 text-xs text-amber-700">Auto-renew is off. You stay featured until {profile.featured_until ? new Date(profile.featured_until).toLocaleDateString() : "the period ends"}.</p>
            : <p className="mt-1 text-xs text-slate-500">Renews automatically each month. Cancel anytime — you keep featured until the paid period ends.</p>}
          {!willCancel && (
            <Button data-testid="feature-cancel-sub" onClick={cancelSub} disabled={cancelling} variant="outline" className="mt-3 rounded-full border-red-300 text-red-600 hover:bg-red-50">
              {cancelling ? "Cancelling…" : "Cancel auto-renew"}
            </Button>
          )}
        </div>
      ) : soldOut ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white/70 p-4" data-testid="featured-soldout">
          <p className="text-sm font-semibold text-slate-900">Featured slots are full in your service area{avail.cities.length > 1 ? "s" : ""}.</p>
          <p className="mt-1 text-xs text-slate-500">Only {avail.cap} businesses per city can be featured at once. We'll open a slot as soon as one frees up — check back soon.</p>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-3">
            {plans.map((p) => (
              <Button key={p.id} data-testid={`feature-plan-${p.id}`} onClick={() => buy(p.id)} disabled={!!busy}
                className="bg-slate-950 hover:bg-amber-600 text-white rounded-full">
                {busy === p.id ? "Redirecting…" : `${p.label} — $${p.amount} CAD`}
              </Button>
            ))}
          </div>
          {sub && (
            <div className="pt-1">
              <Button data-testid="feature-plan-sub_monthly" onClick={() => buy(sub.id)} disabled={!!busy}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-full">
                {busy === sub.id ? "Redirecting…" : `${sub.label} — $${sub.amount} CAD/mo`}
              </Button>
              <span className="ml-3 text-xs text-slate-500">Best value · stays featured, auto-renews monthly</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileTab({ profile, onSaved }) {
  const [form, setForm] = useState({
    business_name: profile.business_name, bio: profile.bio || "", phone: profile.phone || "",
    trades: profile.trades || [], service_areas: profile.service_areas || [], photos: profile.photos || [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try { await api.put("/businesses/me", form); toast.success("Profile saved"); onSaved(); }
    catch { toast.error("Save failed"); }
  };
  const submit = async () => {
    try { await api.post("/businesses/me/submit"); toast.success("Submitted for vetting!"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot submit"); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
      <div>
        <label className="text-sm font-semibold text-slate-700">Business name</label>
        <Input data-testid="profile-name-input" className="mt-2 h-11" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} />
      </div>
      <div>
        <label className="text-sm font-semibold text-slate-700">About</label>
        <Textarea data-testid="profile-bio-input" className="mt-2" value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Tell clients about your work…" />
      </div>
      <div>
        <label className="text-sm font-semibold text-slate-700">Phone</label>
        <Input data-testid="profile-phone-input" className="mt-2 h-11" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
      </div>
      <div>
        <label className="text-sm font-semibold text-slate-700">Trades / services</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {TRADES.map((t) => (
            <button key={t} data-testid={`profile-trade-${t}`} onClick={() => set("trades", toggle(form.trades, t))}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${form.trades.includes(t) ? "bg-slate-950 text-white border-slate-950" : "bg-white text-slate-600 border-slate-200"}`}>{t}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-sm font-semibold text-slate-700">Service areas</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {AREAS.map((a) => (
            <button key={a} data-testid={`profile-area-${a}`} onClick={() => set("service_areas", toggle(form.service_areas, a))}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${form.service_areas.includes(a) ? "bg-amber-600 text-white border-amber-600" : "bg-white text-slate-600 border-slate-200"}`}>{a}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <Button data-testid="profile-save-button" onClick={save} variant="outline" className="rounded-full">Save profile</Button>
        {profile.status === "draft" || profile.status === "rejected" ? (
          <Button data-testid="profile-submit-vetting-button" onClick={submit} className="bg-slate-950 hover:bg-amber-600 text-white rounded-full">Submit for vetting</Button>
        ) : null}
      </div>
    </div>
  );
}

function DocsTab({ docs, profile, onChanged }) {
  const byType = Object.fromEntries(docs.map((d) => [d.doc_type, d]));
  const [uploading, setUploading] = useState("");
  const [expiry, setExpiry] = useState("");

  const upload = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/documents", { doc_type: docType, file_id: up.data.file_id, filename: up.data.filename, expiry: docType === "insurance" ? expiry : null });
      toast.success("Document uploaded"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(""); }
  };

  const referral = async () => {
    try { await api.post("/businesses/me/insurance-referral"); toast.success("Insurance referral requested"); onChanged(); }
    catch { toast.error("Failed"); }
  };

  const statusIcon = (s) => s === "approved" ? <CheckCircle2 size={16} className="text-emerald-600" /> : s === "rejected" ? <XCircle size={16} className="text-red-600" /> : <Clock size={16} className="text-amber-600" />;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Umbrella size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-slate-900">Need insurance? Get an optional referral</h3>
            <p className="text-sm text-slate-600">We'll connect you with a licensed partner. Black Umbrella never issues or brokers insurance — you still upload proof below for the Insured badge.</p>
          </div>
        </div>
        <Button data-testid="insurance-referral-button" onClick={referral} variant="outline" className="rounded-full whitespace-nowrap">
          {profile.insurance_referral ? "Referral requested" : "Get Insured"}
        </Button>
      </div>

      {DOC_TYPES.map((dt) => {
        const d = byType[dt.key];
        return (
          <div key={dt.key} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-slate-400" />
                <div>
                  <p className="font-semibold text-slate-900">{dt.label}</p>
                  {d && <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">{statusIcon(d.status)} {d.filename} · {d.status}{d.reason ? ` — ${d.reason}` : ""}{d.expiry ? ` · expires ${d.expiry}` : ""}</p>}
                </div>
              </div>
              <label className="cursor-pointer">
                <span data-testid={`contractor-doc-upload-${dt.key}`} className="inline-flex items-center gap-1 text-sm font-semibold px-4 py-2 rounded-full bg-slate-950 text-white hover:bg-amber-600 transition-colors">
                  <Upload size={14} /> {uploading === dt.key ? "Uploading…" : d ? "Replace" : "Upload"}
                </span>
                <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => upload(dt.key, e.target.files[0])} />
              </label>
            </div>
            {dt.key === "insurance" && (
              <div className="mt-3">
                <label className="text-xs font-semibold text-slate-500">Set policy expiry date before uploading</label>
                <Input data-testid="insurance-expiry-input" type="date" className="mt-1 h-10 w-52" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function JobsTab({ jobs, profile, onQuoted }) {
  if (profile.status !== "approved")
    return <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">Get approved through vetting to see and quote on jobs in your area.</div>;
  if (jobs.length === 0)
    return <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No matching jobs right now. Jobs in your trades and service areas appear here.</div>;
  return (
    <div className="space-y-4">
      {jobs.map((j) => <JobQuoteCard key={j.id} job={j} onQuoted={onQuoted} />)}
    </div>
  );
}

function JobQuoteCard({ job, onQuoted }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState({ amount: "", labor: "", materials: "", timeline_days: 7, message: "" });
  const [aiBusy, setAiBusy] = useState(false);
  const aiDraft = async () => {
    setAiBusy(true);
    try {
      const r = await api.post("/ai/quote-assist", { job_title: job.title, notes: q.message });
      setQ((s) => ({ ...s, message: r.data.message }));
      toast.success("Draft ready — review & edit");
    } catch { toast.error("AI is busy, try again"); }
    finally { setAiBusy(false); }
  };
  const submit = async () => {
    try {
      await api.post(`/jobs/${job.id}/quotes`, {
        amount: parseFloat(q.amount) || 0, labor: parseFloat(q.labor) || 0,
        materials: parseFloat(q.materials) || 0, timeline_days: parseInt(q.timeline_days) || 7, message: q.message,
      });
      toast.success("Quote submitted"); setOpen(false); onQuoted();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center justify-between gap-3">
      <div>
        <h3 className="font-semibold text-slate-900">{job.title}</h3>
        <p className="text-sm text-slate-500">{job.trade} · {job.city} · {job.timing}</p>
        <p className="text-sm text-slate-500 mt-1 line-clamp-1">{job.description}</p>
      </div>
      {job.already_quoted ? (
        <span className="text-sm text-emerald-700 font-semibold whitespace-nowrap">Quoted</span>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={`quote-open-${job.id}`} className="bg-amber-600 hover:bg-amber-700 text-white rounded-full whitespace-nowrap">Submit quote</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Quote — {job.title}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-xs font-semibold text-slate-500">Total ($)</label><Input data-testid="quote-amount-input" type="number" value={q.amount} onChange={(e) => setQ({ ...q, amount: e.target.value })} /></div>
                <div><label className="text-xs font-semibold text-slate-500">Labor ($)</label><Input type="number" value={q.labor} onChange={(e) => setQ({ ...q, labor: e.target.value })} /></div>
                <div><label className="text-xs font-semibold text-slate-500">Materials ($)</label><Input type="number" value={q.materials} onChange={(e) => setQ({ ...q, materials: e.target.value })} /></div>
              </div>
              <div><label className="text-xs font-semibold text-slate-500">Timeline (days)</label><Input data-testid="quote-timeline-input" type="number" value={q.timeline_days} onChange={(e) => setQ({ ...q, timeline_days: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Message</label><Textarea data-testid="quote-message-input" value={q.message} onChange={(e) => setQ({ ...q, message: e.target.value })} placeholder="Scope, approach, availability…" />
                <button type="button" data-testid="ai-quote-draft-btn" onClick={aiDraft} disabled={aiBusy} className="mt-1 text-xs font-semibold text-amber-700 hover:underline">{aiBusy ? "Drafting…" : "✨ Draft with AI"}</button>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="quote-submit-button" onClick={submit} className="bg-slate-950 hover:bg-amber-600 text-white rounded-full">Send quote</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function PayoutsTab({ myJobs, profile, onChanged }) {
  const [connect, setConnect] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [redirecting, setRedirecting] = useState(false);
  useEffect(() => {
    api.get("/businesses/me/connect/status").then((r) => setConnect(r.data)).catch(() => {});
    api.get("/businesses/me/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  }, []);

  const setup = async () => {
    setRedirecting(true);
    try {
      const r = await api.post("/businesses/me/connect/account", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start Stripe onboarding"); setRedirecting(false); }
  };

  const active = myJobs.filter((j) => ["booked", "in_progress", "completed"].includes(j.status));
  const advance = async (job, status) => {
    try { await api.patch(`/jobs/${job.id}/status`, { status }); toast.success("Updated"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const bankReady = connect?.bank_ready;
  const needsVerify = connect?.verification_needed;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="payouts-onboarding-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <Landmark size={20} className="text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-slate-900">Payouts via Stripe</h3>
              <p className="text-sm text-slate-500 max-w-md">Connect your bank through Stripe to receive real payouts (job total minus 15% commission) when a client releases escrow.</p>
              {connect && (
                <span data-testid="payouts-status-badge" className={`mt-2 inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full ${bankReady ? "bg-emerald-100 text-emerald-700" : (needsVerify || connect.connected) ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                  {bankReady ? "Payouts enabled" : needsVerify ? "Verify identity to enable bank payouts" : connect.connected ? "Onboarding incomplete" : "Not connected"}
                </span>
              )}
              {needsVerify && (
                <p className="mt-1.5 text-xs text-amber-700 max-w-md">Escrow releases already reach this account's Stripe balance, but Stripe still needs to finish verifying the owner (e.g. identity / proof of liveness) before paying out to a bank. Click below to finish verification.</p>
              )}
            </div>
          </div>
          <Button data-testid="stripe-connect-button" onClick={setup} disabled={redirecting}
            className="bg-slate-950 hover:bg-amber-600 text-white rounded-full whitespace-nowrap">
            {redirecting ? "Redirecting…" : bankReady ? "Manage payouts" : needsVerify ? "Finish verification" : connect?.connected ? "Finish setup" : "Set up payouts"}
          </Button>
        </div>
      </div>

      {payouts.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="payouts-list">
          <h3 className="font-semibold text-slate-900 mb-3">Payout history</h3>
          <div className="space-y-2">
            {payouts.map((p) => (
              <div key={p.job_id} data-testid={`payout-row-${p.job_id}`} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                <div>
                  <p className="font-medium text-slate-800">{p.job_title}</p>
                  <p className="text-xs text-slate-500">{money(p.business_payout || 0)} payout</p>
                </div>
                <div className="text-right">
                  {p.status === "refunded" ? (
                    <span className="text-xs font-semibold text-red-600">Refunded</span>
                  ) : p.released && p.payout_mode === "stripe_transfer" ? (
                    <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={13} /> Payout sent</span>
                  ) : p.released ? (
                    <span className="text-xs font-semibold text-amber-700">Recorded — enable bank payouts</span>
                  ) : (
                    <span className="text-xs font-semibold text-slate-500">Held in escrow</span>
                  )}
                  {p.transfer_id && <p className="text-[10px] text-slate-400 font-mono">{p.transfer_id}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {active.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No booked jobs yet. Accepted quotes will appear here.</div>
      ) : active.map((j) => (
        <div key={j.id} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center justify-between gap-3">
          <div>
            <Link to={`/jobs/${j.id}`} className="font-semibold text-slate-900 hover:text-amber-600">{j.title}</Link>
            <p className="text-sm text-slate-500">{j.trade} · {j.city}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={j.status} />
            {j.status === "booked" && <Button data-testid={`job-start-${j.id}`} onClick={() => advance(j, "in_progress")} className="rounded-full bg-slate-950 hover:bg-amber-600 text-white">Start work</Button>}
            {j.status === "in_progress" && <Button data-testid={`job-complete-${j.id}`} onClick={() => advance(j, "completed")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">Mark complete</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}
